import { useState } from "react";
import { useFetch } from "@/hooks/useFetch";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { FileDown, FileSpreadsheet, Check, Send, Ban, RefreshCw } from "lucide-react";
import { rupiah, tanggal } from "@/lib/format";
import { BillingDetail, openPdf } from "@/pages/Proforma";

export default function Invoices() {
  const { data, loading, reload } = useFetch("/invoices");
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const openDetail = async (row) => { const r = await api.get(`/invoices/${row.id}`); setSel(r.data); };
  const refresh = async () => { reload(); if (sel) { const r = await api.get(`/invoices/${sel.id}`); setSel(r.data); } };

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };
  const setStatus = (s) => act(() => api.post(`/invoices/${sel.id}/status`, { status: s }), `Status: ${s}`);
  const cancel = () => act(() => api.post(`/invoices/${sel.id}/cancel`, { reason: cancelReason }), "Invoice dibatalkan");

  return (
    <div>
      <PageHeader title="Invoice" subtitle="Format nomor INV/YYYY/MM/0001 — snapshot permanen saat terbit" />
      {loading ? <div className="h-64 rounded-md bg-muted animate-pulse" /> : (
        <DataTable testId="invoices" data={data || []} onRowClick={openDetail}
          columns={[
            { key: "number", label: "No. Invoice" },
            { key: "date", label: "Tanggal", render: (r) => tanggal(r.date) },
            { key: "billing_start", label: "Periode", render: (r) => `${tanggal(r.billing_start)} – ${tanggal(r.billing_end)}` },
            { key: "grand_total", label: "Grand Total", numeric: true, render: (r) => rupiah(r.grand_total) },
            { key: "paid_amount", label: "Dibayar", numeric: true, render: (r) => rupiah(r.paid_amount || 0) },
            { key: "source_proforma_id", label: "Sumber", render: (r) => r.source_proforma_id ? "Proforma" : "Langsung" },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} />
      )}

      <Sheet open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <SheetContent className="w-full sm:max-w-3xl overflow-y-auto scrollbar-thin">
          {sel && (
            <>
              <SheetHeader><SheetTitle className="font-display flex items-center gap-3">{sel.number} <StatusBadge status={sel.status} /></SheetTitle></SheetHeader>
              <BillingDetail doc={sel} />
              <div className="flex flex-wrap gap-2 mt-6 pt-4 border-t">
                <Button variant="outline" size="sm" onClick={() => openPdf(`/invoices/${sel.id}/pdf`)} data-testid="invoice-pdf-btn"><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                <Button variant="outline" size="sm" onClick={() => openPdf(`/invoices/${sel.id}/excel`)} data-testid="invoice-excel-btn"><FileSpreadsheet className="h-4 w-4 mr-1" /> Excel</Button>
                {sel.status === "Draft" && <Button variant="outline" size="sm" onClick={() => setStatus("Menunggu Persetujuan")} disabled={busy} data-testid="invoice-submit-btn"><Send className="h-4 w-4 mr-1" /> Ajukan Persetujuan</Button>}
                {sel.status === "Menunggu Persetujuan" && <Button variant="outline" size="sm" onClick={() => setStatus("Disetujui")} disabled={busy} data-testid="invoice-approve-btn"><Check className="h-4 w-4 mr-1" /> Setujui</Button>}
                {["Disetujui", "Menunggu Persetujuan"].includes(sel.status) && <Button size="sm" onClick={() => setStatus("Terbit")} disabled={busy} data-testid="invoice-issue-btn" className="bg-[#9333EA] hover:bg-[#7e22ce]"><Check className="h-4 w-4 mr-1" /> Terbitkan</Button>}
                {sel.status === "Terbit" && <Button variant="outline" size="sm" onClick={() => setStatus("Terkirim")} disabled={busy}><Send className="h-4 w-4 mr-1" /> Tandai Terkirim</Button>}
                {!["Dibatalkan", "Lunas"].includes(sel.status) && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="outline" size="sm" className="text-rose-600 border-rose-200" data-testid="invoice-cancel-btn"><Ban className="h-4 w-4 mr-1" /> Batalkan</Button></AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader><AlertDialogTitle>Batalkan Invoice {sel.number}?</AlertDialogTitle>
                        <AlertDialogDescription>Nomor invoice tetap tersimpan (tidak dihapus). Alasan pembatalan wajib diisi.</AlertDialogDescription></AlertDialogHeader>
                      <div className="space-y-1.5"><Label>Alasan Pembatalan</Label><Input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="cancel-reason-input" /></div>
                      <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={cancel} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-cancel-btn">Konfirmasi Pembatalan</AlertDialogAction></AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              {sel.cancel_reason && <p className="text-sm text-rose-600 mt-3">Dibatalkan: {sel.cancel_reason}</p>}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
